import React, { useState, useEffect } from "react";
import { PageTitle } from "../../components";
import {
  Table,
  Space,
  Button,
  Form,
  Input,
  Select,
  Modal,
  Tabs,
  message,
  Skeleton,
  Result,
} from "antd";
import type { ColumnsType } from "antd/lib/table";
import dayjs from "dayjs";

import { Routes, Route, useParams, Link, useNavigate } from "react-router-dom";
import { CURRENT_CLUB_ID } from "../../_helpers/constants";
import { usersApi, userClubsApi, teamsApi } from "../../_helpers/apiService";

const { Option } = Select;
const { TabPane } = Tabs;

interface DataType {
  key?: string;
  lastName?: string;
  firstName?: string;
}

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 14 },
};

function Leader() {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [data, setData] = useState<DataType>({});
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);
  const [teams, setTeams] = useState<any[]>([]);
  const [userClubId, setUserClubId] = useState<number | null>(null);

  let { leaderId } = useParams();

  useEffect(() => {
    fetchTeams();
    if (leaderId) {
      fetchLeader();
    }
  }, [leaderId]);

  const fetchTeams = async () => {
    try {
      const response = await teamsApi.getAll({
        club_id: CURRENT_CLUB_ID,
        limit: 1000
      });
      setTeams(response.data.items);
    } catch (error: any) {
      console.error("Fetch teams error:", error);
    }
  };

  const fetchLeader = async () => {
    setLoading(true);
    try {
      // Fetch user with club details to check is_team_leader
      const response = await userClubsApi.getWithDetails(CURRENT_CLUB_ID, {
        user_id: parseInt(leaderId!),
        is_team_leader: true,
        limit: 1
      });

      // Check if the user is a team leader in this club
      if (response.data.items && response.data.items.length > 0) {
        const userData = response.data.items[0];
        setData({
          lastName: userData.last_name,
          firstName: userData.first_name,
        });
        form.setFieldsValue({
          email: userData.email,
          lastName: userData.last_name,
          firstName: userData.first_name,
          phoneNumber: userData.phone_number?.replace('+976', '') || '',
          teamId: userData.team_id,
        });

        // Get the user_club record ID for updating
        const userClubResponse = await userClubsApi.getAll({
          club_id: CURRENT_CLUB_ID,
          user_id: parseInt(leaderId!)
        });
        if (userClubResponse.data.items.length > 0) {
          setUserClubId(userClubResponse.data.items[0].id);
        }

        setHasAccess(true);
      } else {
        setHasAccess(false);
      }
    } catch (error: any) {
      console.error("Fetch leader error:", error);
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  const onFinish = () => {
    form
      .validateFields()
      .then(async (values) => {
        if (leaderId) {
          try {
            // Update user info
            await usersApi.update(parseInt(leaderId), {
              email: values.email,
              first_name: values.firstName,
              last_name: values.lastName,
              phone_number: values.phoneNumber.startsWith('+976')
                ? values.phoneNumber
                : '+976' + values.phoneNumber,
            });

            // Update team in user_club if userClubId exists
            if (userClubId && values.teamId) {
              await userClubsApi.update(userClubId, {
                team_id: values.teamId
              });

              // Update team's leader_id
              await teamsApi.update(values.teamId, {
                leader_id: parseInt(leaderId)
              });
            }

            message.success("Амжилттай.");
            fetchLeader(); // Refresh data
          } catch (error: any) {
            console.error("Update leader error:", error);
            message.error("Тэргүүлэгчийн мэдээлэл шинэчлэхэд алдаа гарлаа");
          }
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  if (loading) {
    return (
      <>
        <PageTitle loading={true} title="Уншиж байна..." right={null} />
        <div className="page-content-wrapper">
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-12">
                <div className="card">
                  <div className="card-body">
                    <Skeleton loading={true} active />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!hasAccess) {
    return (
      <>
        <PageTitle title="Хандах эрхгүй" right={null} />
        <div className="page-content-wrapper">
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-12">
                <div className="card">
                  <div className="card-body">
                    <Result
                      status="403"
                      title="Хандах эрхгүй"
                      subTitle="Уучлаарай, тэргүүлэгчийн мэдээлэл олдсонгүй."
                      extra={
                        <Button type="primary" onClick={() => navigate("/leaders")}>
                          Буцах
                        </Button>
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {data ? (
        <PageTitle
          loading={loading}
          title={`${data?.lastName} ${data?.firstName}`}
          description={"Тэргүүлэгчийн профайл"}
          right={null}
          breadcrumbItems={[
            { link: "/leaders", title: "Тэргүүлэгчид" },
            { title: `${data?.lastName} ${data?.firstName}` },
          ]}
        />
      ) : (
        ""
      )}
      <div className="page-content-wrapper">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <Skeleton loading={loading} active>
                    <Form {...formItemLayout} form={form} onFinish={onFinish}>
                      <Form.Item
                        label="Имэйл хаяг"
                        name="email"
                        rules={[
                          { required: true, message: "Имэйл хаяг оруулна уу!" },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        label="Овог"
                        name="lastName"
                        rules={[
                          { required: true, message: "Овог оруулна уу!" },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        label="Нэр"
                        name="firstName"
                        rules={[{ required: true, message: "Нэр оруулна уу!" }]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        label="Утасны дугаар"
                        name="phoneNumber"
                        rules={[
                          {
                            required: true,
                            message: "Утасны дугаар оруулна уу!",
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        label="Баг"
                        name="teamId"
                        rules={[
                          {
                            required: true,
                            message: "Баг сонгоно уу!",
                          },
                        ]}
                      >
                        <Select
                          placeholder="Баг сонгоно уу"
                          allowClear
                          showSearch
                          optionFilterProp="children"
                        >
                          {teams
                            .sort((a: any, b: any) => (a.name > b.name ? 1 : -1))
                            .map((team: any) => (
                              <Option key={team.id} value={team.id}>
                                {team.name}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                      <Form.Item wrapperCol={{ offset: 6, span: 16 }}>
                        <Button type="primary" htmlType="submit">
                          Хадгалах
                        </Button>
                      </Form.Item>
                    </Form>
                  </Skeleton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default Leader;
